// // src/components/chat/ChatWindow.jsx
// import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import styles from './ChatWindow.module.css';
// import {
//   useGetPersonalMessagesQuery,
//   useSendMessageMutation,
//   useMarkAsReadMutation,
//   useGetChatsQuery,
//   useLazyLoadMoreMessagesQuery,
//   useClearChatMutation,
//   useRemoveUserMutation,
// } from '../../store/api/chatApi';
// import signalRService from '../../services/signalrService';
// import MessageItem from './MessageItem';
// import { formatLocalDate } from '../../utils/dateTime';
// import { useOnlineStatus } from '../../hooks/useOnlineStatus';

// // Maximum allowed attachment size (10 MB)
// const MAX_FILE_BYTES = 10 * 1024 * 1024;

// // ─── Tiny reusable inline error banner ──────────────────────────────────────
// function InlineError({ message, onDismiss }) {
//   if (!message) return null;
//   return (
//     <div className={styles.inlineError} role="alert">
//       <span>{message}</span>
//       <button
//         className={styles.inlineErrorDismiss}
//         onClick={onDismiss}
//         aria-label="Dismiss error"
//         type="button"
//       >
//         ✕
//       </button>
//     </div>
//   );
// }

// // ─── Skeleton loader — shown while messages are fetching ─────────────────────
// //
// // Renders a set of fake chat bubbles with a shimmer animation.
// // Layout mirrors the real message list (alternating mine/theirs, varied widths)
// // so the UI feels stable and the user knows exactly what's coming.
// function MessageSkeleton() {
//   // Predetermined pattern: [isMine, widthPercent]
//   // Varied widths make it look natural rather than mechanical.
//   const rows = [
//     [false, 55],
//     [true,  40],
//     [false, 72],
//     [true,  60],
//     [false, 48],
//     [true,  65],
//     [false, 38],
//     [true,  52],
//   ];

//   return (
//     <div className={styles.skeletonContainer} aria-hidden="true">
//       {rows.map(([isMine, width], i) => (
//         <div
//           key={i}
//           className={[
//             styles.skeletonRow,
//             isMine ? styles.skeletonRowMine : styles.skeletonRowTheirs,
//           ].join(' ')}
//         >
//           {/* Avatar dot on the left for incoming messages */}
//           {!isMine && <div className={styles.skeletonAvatar} />}

//           <div
//             className={styles.skeletonBubble}
//             style={{ width: `${width}%` }}
//           />
//         </div>
//       ))}
//     </div>
//   );
// }

// // ─── Main component ──────────────────────────────────────────────────────────
// export default function ChatWindow({
//   conversationId,
//   currentUserId,
//   selectedUser,
//   isNewChat = false,
//   isMobileVisible = false,
//   onMobileBack,
// }) {
//   const navigate = useNavigate();

//   // ── Input state ────────────────────────────────────────────────────────────
//   const [message, setMessage] = useState('');
//   const [mediaFile, setMediaFile] = useState(null);
//   const [mediaPreview, setMediaPreview] = useState(null);
//   const [mediaFileName, setMediaFileName] = useState(null); // for non-image files

//   // ── Atomic message state ──────────────────────────────────────────────────
//   //
//   // WHY atomic: `conversationId` (prop) and message data (state) are separate
//   // React values — they update at different points in the render cycle. Between
//   // a render that changes `conversationId` and the effect that clears stale
//   // messages, there is a paint where the component renders with the NEW
//   // conversationId but OLD allMessages. The user briefly sees the previous
//   // conversation's messages (the "ghost" bug on reload + switch).
//   //
//   // Bundling conversationId together with the messages in a single state object
//   // makes it impossible for them to disagree: one atomic setMsgState call
//   // updates both simultaneously. The render derives displayable messages with a
//   // one-line guard — if the stored conversationId doesn't match the prop, it
//   // shows [] instead of stale data.
//   const [msgState, setMsgState] = useState({
//     conversationId: null,
//     messages: [],
//     cursor: null,
//     hasMore: false,
//   });

//   // ── UI state ───────────────────────────────────────────────────────────────
//   const [showMenu, setShowMenu] = useState(false);

//   // ── Inline error states (replaces every blocking alert() call) ────────────
//   const [sendError, setSendError] = useState(null);
//   const [paginationError, setPaginationError] = useState(null);
//   const [actionError, setActionError] = useState(null);

//   // ── Refs ───────────────────────────────────────────────────────────────────
//   const bottomRef = useRef(null);
//   const inputRef = useRef(null);
//   const fileInputRef = useRef(null);
//   const messageAreaRef = useRef(null);
//   const menuWrapperRef = useRef(null);

//   /**
//    * Tracks which conversationId the current `msgState` reflects.
//    * Updated ONLY when real data is committed — never while loading.
//    *
//    * Hard-reload invariant:
//    *   1. conversationId = userId, messagesData = undefined (fetch in-flight)
//    *      → ref stays null → state is cleared, nothing more
//    *   2. messagesData arrives → ref is still null → isConversationSwitch = true
//    *      → REPLACE path fires → cursor / hasMore / messages all set ✓
//    *
//    *   Setting the ref in step 1 would cause step 2 to see
//    *   isConversationSwitch = false → MERGE path → setCursor/setHasMore
//    *   never called → "Load older messages" button never appears.
//    */
//   const syncedConversationRef = useRef(null);
//   const hasMarkedAsReadRef = useRef(false);
//   const prevScrollHeightRef = useRef(0);

//   /**
//    * Set by the sync effect after a conversation switch with real data.
//    * Read by the scroll effect (post-DOM-commit) to trigger instant scroll.
//    * A ref avoids the extra render cycle that useState would require.
//    */
//   const shouldScrollToBottomRef = useRef(false);

//   // ── API hooks ──────────────────────────────────────────────────────────────
//   const { data: chats = [] } = useGetChatsQuery();

//   /**
//    * Memoised — prevents chats.some() from running on every render and keeps
//    * the "Say hello!" hint stable.
//    */
//   const isTrulyNewChat = useMemo(
//     () => isNewChat || !chats.some((c) => c.otherUserId === conversationId),
//     [isNewChat, chats, conversationId]
//   );

//   const {
//     // `currentData` — NOT `data`.
//     //
//     // RTK Query's `data` field retains the last successful result for ANY
//     // query argument even after the arg changes. So switching from UserA →
//     // UserB leaves `data` pointing at UserA's messages until UserB's fetch
//     // resolves. The sync effect sees non-undefined `messagesData`, hits the
//     // REPLACE path, and writes UserA's messages under UserB's conversationId.
//     //
//     // `currentData` is immediately `undefined` when the arg changes and is
//     // only populated once the CURRENT arg's fetch resolves — which is exactly
//     // the behaviour the sync effect relies on to distinguish "loading" from
//     // "data ready". One field name change; fixes the cross-conversation
//     // contamination on reload + switch.
//     currentData: messagesData,
//     isLoading,
//     error: fetchError,
//     refetch,
//   } = useGetPersonalMessagesQuery(conversationId, {
//     skip: !conversationId,
//     refetchOnMountOrArgChange: true,
//   });

//   const [loadMoreMessages, { isLoading: isLoadingMore }] =
//     useLazyLoadMoreMessagesQuery();
//   const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
//   const [markAsRead] = useMarkAsReadMutation();
//   const [clearChat] = useClearChatMutation();
//   const [removeUser] = useRemoveUserMutation();

//   /**
//    * Derive unread count as a primitive to keep handleScroll's useCallback
//    * deps stable. Without this, `existingChat` (object reference) changes on
//    * every render, recreating the scroll handler on each paint.
//    */
//   const unreadCount = useMemo(
//     () => chats.find((c) => c.otherUserId === conversationId)?.unreadCount ?? 0,
//     [chats, conversationId]
//   );

//   // ── Derive safe display values from atomic msgState ─────────────────────────
//   //
//   // The render guard (msgState.conversationId === conversationId) is the final
//   // line of defence: even if some async path manages to set msgState to the
//   // wrong conversation, the render will ALWAYS show [] and hasMore=false until
//   // the state is correct. This replaces the previous pattern of separately
//   // guarding every setAllMessages / setCursor / setHasMore call.
//   const safeMessages = msgState.conversationId === conversationId
//     ? msgState.messages
//     : [];
//   const safeCursor  = msgState.conversationId === conversationId
//     ? msgState.cursor
//     : null;
//   const safeHasMore = msgState.conversationId === conversationId
//     ? msgState.hasMore
//     : false;

//   // ═══════════════════════════════════════════════════════════════════════════
//   // EFFECTS
//   // ═══════════════════════════════════════════════════════════════════════════

//   // ── 1. Message-state sync ─────────────────────────────────────────────────
//   useEffect(() => {
//     if (!conversationId) return;

//     const isConversationSwitch = conversationId !== syncedConversationRef.current;

//     if (!messagesData) {
//       // Fetch in-flight or errored. Write an EMPTY atomic state stamped with
//       // the current conversationId. The render guard
//       // (msgState.conversationId === conversationId) will immediately show []
//       // on the very next paint — no window where stale messages are visible.
//       //
//       // IMPORTANT: do NOT update syncedConversationRef here (see header comment
//       // above for the hard-reload invariant this protects).
//       if (isConversationSwitch) {
//         setMsgState({ conversationId, messages: [], cursor: null, hasMore: false });
//       }
//       return;
//     }

//     if (isConversationSwitch) {
//       // ── Conversation switched and data ready → full atomic replace ────────
//       syncedConversationRef.current = conversationId;
//       setMsgState({
//         conversationId,
//         messages: messagesData.messages ?? [],
//         cursor: messagesData.nextCursor ?? null,
//         hasMore: messagesData.hasMore ?? false,
//       });
//       shouldScrollToBottomRef.current = true;
//     } else {
//       // ── Same conversation, cache updated → incremental merge ──────────────
//       // Covers two cases:
//       //   a) SignalR pushed a new message into the RTK cache.
//       //   b) An optimistic `temp-xxx` was confirmed with a real server ID.
//       //      Stale temp entries are filtered out to prevent double-rendering.
//       //
//       // SAFETY: snapshot both identifiers before the async functional updater.
//       // React 18 can defer functional updaters; by the time the updater runs,
//       // syncedConversationRef may have advanced. The guard ensures we never
//       // merge one conversation's data into another's.
//       const mergeForConversation = conversationId;
//       const snapshotMessages = messagesData.messages ?? [];

//       setMsgState((prev) => {
//         // Guard: if the conversation changed before this updater ran, bail out.
//         // Also validates the stored conversationId to be doubly safe.
//         if (
//           syncedConversationRef.current !== mergeForConversation ||
//           prev.conversationId !== mergeForConversation
//         ) {
//           return prev;
//         }

//         const cacheIds = new Set(snapshotMessages.map((m) => m.id));

//         const map = new Map(
//           prev.messages
//             .filter((m) => !String(m.id).startsWith('temp-') || cacheIds.has(m.id))
//             .map((m) => [m.id, m])
//         );

//         snapshotMessages.forEach((m) => map.set(m.id, m));

//         return {
//           ...prev,
//           messages: Array.from(map.values()).sort(
//             (a, b) => new Date(a.sentAt) - new Date(b.sentAt)
//           ),
//         };
//       });
//     }
//   }, [messagesData, conversationId]);

//   // ── 2. UI-only reset on conversation change ────────────────────────────────
//   useEffect(() => {
//     if (!conversationId) return;
//     hasMarkedAsReadRef.current = false;
//     setMessage('');
//     setMediaFile(null);
//     setMediaPreview(null);
//     setMediaFileName(null);
//     setSendError(null);
//     setPaginationError(null);
//     setActionError(null);
//     if (fileInputRef.current) fileInputRef.current.value = '';
//     inputRef.current?.focus();
//   }, [conversationId]);

//   // ── 3. Close dropdown on outside click ────────────────────────────────────
//   useEffect(() => {
//     const onMouseDown = (e) => {
//       if (menuWrapperRef.current && !menuWrapperRef.current.contains(e.target)) {
//         setShowMenu(false);
//       }
//     };
//     document.addEventListener('mousedown', onMouseDown);
//     return () => document.removeEventListener('mousedown', onMouseDown);
//   }, []);

//   // ── 4. Mark as read on mount / conversation change ─────────────────────────
//   useEffect(() => {
//     if (conversationId && unreadCount > 0 && !hasMarkedAsReadRef.current) {
//       markAsRead(conversationId);
//       hasMarkedAsReadRef.current = true;
//     }
//   }, [conversationId, unreadCount, markAsRead]);

//   // ── 5. Scroll to bottom ───────────────────────────────────────────────────
//   //
//   // WHY `allMessages.length` instead of the full `allMessages` array:
//   //
//   //   Using the whole array as a dep fires on every merge, including when a
//   //   message's `isRead` flag updates or a `temp-xxx` ID is swapped for a
//   //   real one — both leave the LENGTH unchanged. Those updates must NOT
//   //   trigger a scroll (user may be reading older messages).
//   //
//   //   `.length` changes only when a message is actually added or the list is
//   //   replaced (0 → N on conversation switch) — exactly the two cases where
//   //   we want to consider scrolling.
//   //
//   // WHY shouldScrollToBottomRef still works with this dep:
//   //   On switch, setAllMessages changes length from 0 → N → effect fires →
//   //   shouldScrollToBottomRef is true → instant scroll to bottom ✓
//   useEffect(() => {
//     if (safeMessages.length === 0) return;

//     if (shouldScrollToBottomRef.current) {
//       // Conversation switch / initial load — instant, no animation
//       shouldScrollToBottomRef.current = false;
//       bottomRef.current?.scrollIntoView({ behavior: 'auto' });
//       return;
//     }

//     // New message arrived while viewing — only scroll if near the bottom
//     if (messageAreaRef.current) {
//       const { scrollTop, scrollHeight, clientHeight } = messageAreaRef.current;
//       if (scrollHeight - scrollTop - clientHeight < 200) {
//         bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
//       }
//     }
//   }, [safeMessages.length]); // eslint-disable-line react-hooks/exhaustive-deps

//   // ═══════════════════════════════════════════════════════════════════════════
//   // HANDLERS  — all wrapped in useCallback for stable references
//   // ═══════════════════════════════════════════════════════════════════════════

//   /** Mark conversation as read when user scrolls to the very bottom. */
//   const handleScroll = useCallback(() => {
//     if (!messageAreaRef.current) return;
//     const { scrollTop, scrollHeight, clientHeight } = messageAreaRef.current;
//     if (
//       scrollHeight - scrollTop - clientHeight < 50 &&
//       unreadCount > 0 &&
//       !hasMarkedAsReadRef.current
//     ) {
//       markAsRead(conversationId);
//       hasMarkedAsReadRef.current = true;
//     }
//   }, [conversationId, unreadCount, markAsRead]);

//   /** Cursor-based pagination — load and prepend older messages. */
//   const handleLoadMore = useCallback(async () => {
//     if (!safeHasMore || isLoadingMore || !safeCursor) return;
//     setPaginationError(null);

//     // Snapshot the conversationId BEFORE the await.
//     // If the user switches conversations while the fetch is in-flight,
//     // syncedConversationRef will have moved on. We check it after resolution
//     // and discard the result to prevent older messages from one conversation
//     // being merged into a completely different conversation's message list.
//     const loadingForConversation = conversationId;
//     prevScrollHeightRef.current = messageAreaRef.current?.scrollHeight ?? 0;

//     try {
//       const result = await loadMoreMessages(
//         { otherUserId: loadingForConversation, cursor: safeCursor }
//       ).unwrap();
//       if (!result) return;

//       // Guard: discard if the user navigated away while this was loading
//       if (syncedConversationRef.current !== loadingForConversation) return;

//       setMsgState((prev) => {
//         // Concurrent-mode safety: validate both guards before merging
//         if (
//           syncedConversationRef.current !== loadingForConversation ||
//           prev.conversationId !== loadingForConversation
//         ) return prev;

//         const map = new Map();
//         // Older messages first so they sort to the top
//         [...result.messages, ...prev.messages].forEach((m) => map.set(m.id, m));
//         return {
//           ...prev,
//           messages: Array.from(map.values()).sort(
//             (a, b) => new Date(a.sentAt) - new Date(b.sentAt)
//           ),
//           cursor: result.nextCursor ?? null,
//           hasMore: result.hasMore ?? false,
//         };
//       });

//       // Restore scroll position — prepending must not jump the viewport
//       requestAnimationFrame(() => {
//         if (messageAreaRef.current) {
//           messageAreaRef.current.scrollTop =
//             messageAreaRef.current.scrollHeight - prevScrollHeightRef.current;
//         }
//       });
//     } catch (err) {
//       // Only surface the error if we're still on the same conversation
//       if (syncedConversationRef.current === loadingForConversation) {
//         console.error('Pagination error:', err);
//         setPaginationError('Failed to load older messages. Tap to retry.');
//       }
//     }
//   }, [conversationId, safeCursor, safeHasMore, isLoadingMore, loadMoreMessages]);

//   const handleClearChat = useCallback(async () => {
//     if (!window.confirm('Delete all messages in this chat?')) return;
//     setActionError(null);
//     try {
//       await clearChat(conversationId).unwrap();
//       setMsgState({ conversationId, messages: [], cursor: null, hasMore: false });
//       setShowMenu(false);
//     } catch (err) {
//       console.error('Clear chat failed:', err);
//       setActionError('Failed to delete the chat. Please try again.');
//     }
//   }, [clearChat, conversationId]);

//   const handleRemoveUser = useCallback(async () => {
//     if (!window.confirm('Remove this user from your chat list?')) return;
//     setActionError(null);
//     try {
//       await removeUser(conversationId).unwrap();
//       setShowMenu(false);
//       navigate('/messages');
//     } catch (err) {
//       console.error('Remove user failed:', err);
//       setActionError('Failed to remove user. Please try again.');
//     }
//   }, [removeUser, conversationId, navigate]);

//   const handleSend = useCallback(async () => {
//     if ((!message.trim() && !mediaFile) || isSending) return;
//     setSendError(null);

//     // Snapshot state before clearing so we can restore on failure
//     const text = message.trim();
//     const file = mediaFile;
//     const preview = mediaPreview;
//     const fileName = mediaFileName;

//     // Clear immediately so the UI feels instant
//     setMessage('');
//     setMediaFile(null);
//     setMediaPreview(null);
//     setMediaFileName(null);
//     if (fileInputRef.current) fileInputRef.current.value = '';

//     try {
//       await sendMessage({
//         receiverId: conversationId,
//         message: text || undefined,
//         mediaFile: file || undefined,
//       }).unwrap();
//       inputRef.current?.focus();
//     } catch (err) {
//       console.error('Send failed:', err);
//       // Restore everything so the user doesn't lose their message
//       setMessage(text);
//       setMediaFile(file);
//       setMediaPreview(preview);
//       setMediaFileName(fileName);
//       setSendError('Failed to send. Please try again.');
//     }
//   }, [
//     message,
//     mediaFile,
//     mediaPreview,
//     mediaFileName,
//     isSending,
//     sendMessage,
//     conversationId,
//   ]);

//   /** Enter key handler — stable reference so the input doesn't re-register. */
//   const handleKeyDown = useCallback(
//     (e) => {
//       if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleSend();
//       }
//     },
//     [handleSend]
//   );

//   const handleFileSelect = useCallback((e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     if (file.size > MAX_FILE_BYTES) {
//       setSendError(
//         `File too large — maximum is ${MAX_FILE_BYTES / 1024 / 1024} MB.`
//       );
//       if (fileInputRef.current) fileInputRef.current.value = '';
//       return;
//     }

//     setSendError(null);
//     setMediaFile(file);
//     setMediaFileName(file.name);

//     if (file.type.startsWith('image/')) {
//       const reader = new FileReader();
//       reader.onload = (ev) => setMediaPreview(ev.target.result);
//       reader.readAsDataURL(file);
//     } else {
//       // Non-images show filename in the preview bar — no visual thumbnail
//       setMediaPreview(null);
//     }
//   }, []);

//   const clearMedia = useCallback(() => {
//     setMediaFile(null);
//     setMediaPreview(null);
//     setMediaFileName(null);
//     setSendError(null);
//     if (fileInputRef.current) fileInputRef.current.value = '';
//   }, []);

//   const handleProfileClick = useCallback(() => {
//     if (selectedUser?.id) navigate(`/profile/${selectedUser.id}`);
//   }, [navigate, selectedUser?.id]);

//   const handleToggleMenu = useCallback(
//     () => setShowMenu((prev) => !prev),
//     []
//   );

//   // ── Derived ────────────────────────────────────────────────────────────────
//   // Real-time presence from Redux — updated by SignalR UserOnline/UserOffline events
//   const isUserOnline = useOnlineStatus(selectedUser?.id);

//   const windowClass = [
//     styles.chatWindow,
//     isMobileVisible ? styles.chatVisible : '',
//   ]
//     .filter(Boolean)
//     .join(' ');

//   const canSend = !isSending && (!!message.trim() || !!mediaFile);

//   // ═══════════════════════════════════════════════════════════════════════════
//   // EARLY RENDER STATES
//   // ═══════════════════════════════════════════════════════════════════════════

//   if (!conversationId) {
//     return (
//       <div className={windowClass}>
//         <div className={styles.empty}>
//           <span aria-hidden="true" style={{ fontSize: 40 }}>💬</span>
//           <p>Select a chat to start messaging</p>
//         </div>
//       </div>
//     );
//   }

//   if (isLoading) {
//     return (
//       <div className={windowClass}>
//         {/* Keep the header fully visible while messages load */}
//         <div className={styles.chatHeader}>
//           <button
//             className={styles.backBtn}
//             onClick={onMobileBack}
//             aria-label="Back to conversation list"
//             type="button"
//           >
//             ‹
//           </button>

//           <div className={styles.userInfo}>
//             {selectedUser?.profilePhotoUrl ? (
//               <img
//                 src={selectedUser.profilePhotoUrl}
//                 alt={`${selectedUser.name}'s avatar`}
//                 className={styles.avatar}
//               />
//             ) : (
//               <div className={styles.avatarPlaceholder} aria-hidden="true">
//                 {selectedUser?.name?.charAt(0).toUpperCase() ?? '?'}
//               </div>
//             )}
//             <div className={styles.userMeta}>
//               <span className={styles.userName}>{selectedUser?.name ?? 'User'}</span>
//               <span className={styles.offlineStatus}>Loading…</span>
//             </div>
//           </div>
//         </div>

//         {/* Skeleton message bubbles */}
//         <div
//           className={styles.messageArea}
//           role="status"
//           aria-label="Loading messages"
//           aria-live="polite"
//         >
//           <div className={styles.messageSpacer} />
//           <MessageSkeleton />
//         </div>

//         {/* Disabled input bar — visible but not interactive during load */}
//         <div className={styles.inputContainer}>
//           {/* <button className={styles.attachBtn} disabled type="button" aria-hidden="true">
//             📎
//           </button> */}
//           <input
//             className={styles.input}
//             placeholder="Loading messages…"
//             disabled
//             aria-label="Message input loading"
//           />
//           <button className={styles.sendBtn} disabled type="button">
//             Send
//           </button>
//         </div>
//       </div>
//     );
//   }


//   if (fetchError) {
//     return (
//       <div className={windowClass}>
//         <div className={styles.empty}>
//           <span aria-hidden="true" style={{ fontSize: 36 }}>⚠️</span>
//           <p>Failed to load messages.</p>
//           <button className={styles.retryBtn} onClick={refetch} type="button">
//             Try again
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // ═══════════════════════════════════════════════════════════════════════════
//   // MAIN RENDER
//   // ═══════════════════════════════════════════════════════════════════════════
//   return (
//     <div className={windowClass}>

//       {/* ── Header ─────────────────────────────────────────────────────────── */}
//       <div className={styles.chatHeader}>
//         <button
//           className={styles.backBtn}
//           onClick={onMobileBack}
//           aria-label="Back to conversation list"
//           type="button"
//         >
//           ‹
//         </button>

//         <div className={styles.userInfo}>
//           {selectedUser?.profilePhotoUrl ? (
//             <img
//               src={selectedUser.profilePhotoUrl}
//               alt={`${selectedUser.name}'s avatar`}
//               className={styles.avatar}
//             />
//           ) : (
//             <div className={styles.avatarPlaceholder} aria-hidden="true">
//               {selectedUser?.name?.charAt(0).toUpperCase() ?? '?'}
//             </div>
//           )}

//           <div className={styles.userMeta}>
//             <div
//               className={styles.userName}
//               onClick={handleProfileClick}
//               title="View profile"
//               aria-label={`View ${selectedUser?.name ?? 'user'}'s profile`}
//               type="button"
//             >
//               {selectedUser?.name ?? 'User'}
//             </div>
//             <span
//               className={isUserOnline ? styles.onlineStatus : styles.offlineStatus}
//             >
//               {isUserOnline ? 'Online' : 'Offline'}
//             </span>
//           </div>
//         </div>

//         {/* Context menu */}
//         <div className={styles.menuWrapper} ref={menuWrapperRef}>
//           <button
//             className={styles.menuBtn}
//             onClick={handleToggleMenu}
//             aria-label="Chat options"
//             aria-haspopup="menu"
//             aria-expanded={showMenu}
//             type="button"
//           >
//             ⋮
//           </button>

//           {showMenu && (
//             <div className={styles.dropdown} role="menu">
//               <button
//                 role="menuitem"
//                 onClick={handleClearChat}
//                 type="button"
//               >
//                 🗑 Delete Chat
//               </button>
//               <button
//                 role="menuitem"
//                 onClick={handleRemoveUser}
//                 className={styles.removeOption}
//                 type="button"
//               >
//                 🚫 Remove User
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Clear / remove action errors */}
//       <InlineError
//         message={actionError}
//         onDismiss={() => setActionError(null)}
//       />

//       {/* ── Message area ────────────────────────────────────────────────────── */}
//       <div
//         className={styles.messageArea}
//         ref={messageAreaRef}
//         onScroll={handleScroll}
//         aria-label="Messages"
//         aria-live="polite"
//       >
//         {/*
//           Flex spacer — pushes content to the bottom when messages are few.
//           Collapses to 0 when messages overflow, letting normal scroll take over.
//           Requires: .messageArea { display: flex; flex-direction: column; overflow-y: auto }
//         */}
//         <div className={styles.messageSpacer} />

//         {/* ── Load older messages button ── */}
//         {safeHasMore && (
//           <div className={styles.loadMoreContainer}>
//             {paginationError ? (
//               <button
//                 className={`${styles.loadMoreBtn} ${styles.loadMoreError}`}
//                 onClick={handleLoadMore}
//                 type="button"
//                 aria-label="Retry loading older messages"
//               >
//                 ⚠️ {paginationError}
//               </button>
//             ) : (
//               <button
//                 className={styles.loadMoreBtn}
//                 onClick={handleLoadMore}
//                 disabled={isLoadingMore}
//                 type="button"
//                 aria-label="Load older messages"
//               >
//                 {isLoadingMore ? (
//                   <><span className={styles.loadMoreSpinner} aria-hidden="true" /> Loading…</>
//                 ) : (
//                   <><span aria-hidden="true">⬆</span> Load older messages</>
//                 )}
//               </button>
//             )}
//           </div>
//         )}

//         {/* ── New chat empty state ── */}
//         {isTrulyNewChat && safeMessages.length === 0 && (
//           <div className={styles.newChatHint}>
//             <div className={styles.newChatIcon} aria-hidden="true">👋</div>
//             <p>
//               Say hello to <strong>{selectedUser?.name}</strong>!
//             </p>
//           </div>
//         )}

//         {/* ── Message list ── */}
//         {safeMessages.map((msg, index) => {
//           const showDateSeparator =
//             index === 0 ||
//             new Date(safeMessages[index - 1].sentAt).toDateString() !==
//               new Date(msg.sentAt).toDateString();

//           return (
//             <React.Fragment key={msg.id}>
//               {showDateSeparator && (
//                 <div className={styles.dateSeparator}>
//                   {formatLocalDate(msg.sentAt)}
//                 </div>
//               )}
//               <MessageItem
//                 msg={msg}
//                 isMine={msg.senderId === currentUserId}
//               />
//             </React.Fragment>
//           );
//         })}

//         {/* Scroll anchor — always the last DOM node in the list */}
//         <div ref={bottomRef} />
//       </div>

//       {/* ── Attachment preview bar ──────────────────────────────────────────── */}
//       {(mediaPreview || mediaFileName) && (
//         <div className={styles.mediaPreview}>
//           {mediaPreview ? (
//             <img src={mediaPreview} alt="Attachment preview" />
//           ) : (
//             // Non-image file — show icon + filename
//             <div className={styles.filePreview}>
//               <span className={styles.fileIcon} aria-hidden="true">📎</span>
//               <span className={styles.fileName} title={mediaFileName}>
//                 {mediaFileName}
//               </span>
//             </div>
//           )}
//           <button
//             className={styles.clearMediaBtn}
//             onClick={clearMedia}
//             aria-label="Remove attachment"
//             type="button"
//           >
//             ✕
//           </button>
//         </div>
//       )}

//       {/* Send errors — shown directly above the input bar */}
//       <InlineError
//         message={sendError}
//         onDismiss={() => setSendError(null)}
//       />

//       {/* ── Input bar ───────────────────────────────────────────────────────── */}
//       <div className={styles.inputContainer}>
//         {/* Hidden file input triggered by the attach button */}
//         {/* <input
//           ref={fileInputRef}
//           type="file"
//           accept="image/*,video/*,application/pdf"
//           style={{ display: 'none' }}
//           onChange={handleFileSelect}
//           aria-label="Attach a file"
//         />

//         <button
//           className={styles.attachBtn}
//           onClick={() => fileInputRef.current?.click()}
//           aria-label="Attach file"
//           title="Attach file"
//           type="button"
//           disabled={!!mediaFile} // prevent selecting a second file while one is pending
//         >
//           📎
//         </button> */}

//         <input
//           ref={inputRef}
//           className={styles.input}
//           value={message}
//           onChange={(e) => setMessage(e.target.value)}
//           onKeyDown={handleKeyDown}
//           placeholder="Type a message…"
//           aria-label="Message input"
//         />

//         <button
//           className={styles.sendBtn}
//           onClick={handleSend}
//           disabled={!canSend}
//           aria-label="Send message"
//           type="button"
//         >
//           {isSending ? (
//             <span className={styles.sendingSpinner} aria-hidden="true" />
//           ) : (
//             'Send'
//           )}
//         </button>
//       </div>
//     </div>
//   );
// }

// src/components/chat/ChatWindow.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ChatWindow.module.css';
import {
  useGetPersonalMessagesQuery,
  useSendMessageMutation,
  useMarkAsReadMutation,
  useGetChatsQuery,
  useLazyLoadMoreMessagesQuery,
  useClearChatMutation,
  useRemoveUserMutation,
} from '../../store/api/chatApi';
import signalRService from '../../services/signalrService';
import MessageItem from './MessageItem';
import { formatLocalDate } from '../../utils/dateTime';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

// Maximum allowed attachment size (10 MB)
const MAX_FILE_BYTES = 10 * 1024 * 1024;

// ─── Tiny reusable inline error banner ──────────────────────────────────────
function InlineError({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className={styles.inlineError} role="alert">
      <span>{message}</span>
      <button
        className={styles.inlineErrorDismiss}
        onClick={onDismiss}
        aria-label="Dismiss error"
        type="button"
      >
        ✕
      </button>
    </div>
  );
}

// ─── Skeleton loader — shown while messages are fetching ─────────────────────
//
// Renders a set of fake chat bubbles with a shimmer animation.
// Layout mirrors the real message list (alternating mine/theirs, varied widths)
// so the UI feels stable and the user knows exactly what's coming.
function MessageSkeleton() {
  // Predetermined pattern: [isMine, widthPercent]
  // Varied widths make it look natural rather than mechanical.
  const rows = [
    [false, 55],
    [true,  40],
    [false, 72],
    [true,  60],
    [false, 48],
    [true,  65],
    [false, 38],
    [true,  52],
  ];

  return (
    <div className={styles.skeletonContainer} aria-hidden="true">
      {rows.map(([isMine, width], i) => (
        <div
          key={i}
          className={[
            styles.skeletonRow,
            isMine ? styles.skeletonRowMine : styles.skeletonRowTheirs,
          ].join(' ')}
        >
          {/* Avatar dot on the left for incoming messages */}
          {!isMine && <div className={styles.skeletonAvatar} />}

          <div
            className={styles.skeletonBubble}
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ChatWindow({
  conversationId,
  currentUserId,
  selectedUser,
  isNewChat = false,
  isMobileVisible = false,
  onMobileBack,
  onFirstMessageSent
}) {
  const navigate = useNavigate();

  // ── Input state ────────────────────────────────────────────────────────────
  const [message, setMessage] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaFileName, setMediaFileName] = useState(null); // for non-image files

  // ── Atomic message state ──────────────────────────────────────────────────
  //
  // WHY atomic: `conversationId` (prop) and message data (state) are separate
  // React values — they update at different points in the render cycle. Between
  // a render that changes `conversationId` and the effect that clears stale
  // messages, there is a paint where the component renders with the NEW
  // conversationId but OLD allMessages. The user briefly sees the previous
  // conversation's messages (the "ghost" bug on reload + switch).
  //
  // Bundling conversationId together with the messages in a single state object
  // makes it impossible for them to disagree: one atomic setMsgState call
  // updates both simultaneously. The render derives displayable messages with a
  // one-line guard — if the stored conversationId doesn't match the prop, it
  // shows [] instead of stale data.
  const [msgState, setMsgState] = useState({
    conversationId: null,
    messages: [],
    cursor: null,
    hasMore: false,
  });

  // ── UI state ───────────────────────────────────────────────────────────────
  const [showMenu, setShowMenu] = useState(false);

  // ── Inline error states (replaces every blocking alert() call) ────────────
  const [sendError, setSendError] = useState(null);
  const [paginationError, setPaginationError] = useState(null);
  const [actionError, setActionError] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageAreaRef = useRef(null);
  const menuWrapperRef = useRef(null);

  /**
   * Tracks which conversationId the current `msgState` reflects.
   * Updated ONLY when real data is committed — never while loading.
   *
   * Hard-reload invariant:
   *   1. conversationId = userId, messagesData = undefined (fetch in-flight)
   *      → ref stays null → state is cleared, nothing more
   *   2. messagesData arrives → ref is still null → isConversationSwitch = true
   *      → REPLACE path fires → cursor / hasMore / messages all set ✓
   *
   *   Setting the ref in step 1 would cause step 2 to see
   *   isConversationSwitch = false → MERGE path → setCursor/setHasMore
   *   never called → "Load older messages" button never appears.
   */
  const syncedConversationRef = useRef(null);
  const hasMarkedAsReadRef = useRef(false);
  const prevScrollHeightRef = useRef(0);

  /**
   * Set by the sync effect after a conversation switch with real data.
   * Read by the scroll effect (post-DOM-commit) to trigger instant scroll.
   * A ref avoids the extra render cycle that useState would require.
   */
  const shouldScrollToBottomRef = useRef(false);

  // ── API hooks ──────────────────────────────────────────────────────────────
  const { data: chats = [] } = useGetChatsQuery();

  /**
   * Memoised — prevents chats.some() from running on every render and keeps
   * the "Say hello!" hint stable.
   */
  const isTrulyNewChat = useMemo(
    () => isNewChat || !chats.some((c) => c.otherUserId === conversationId),
    [isNewChat, chats, conversationId]
  );

  const {
    // `currentData` — NOT `data`.
    //
    // RTK Query's `data` field retains the last successful result for ANY
    // query argument even after the arg changes. So switching from UserA →
    // UserB leaves `data` pointing at UserA's messages until UserB's fetch
    // resolves. The sync effect sees non-undefined `messagesData`, hits the
    // REPLACE path, and writes UserA's messages under UserB's conversationId.
    //
    // `currentData` is immediately `undefined` when the arg changes and is
    // only populated once the CURRENT arg's fetch resolves — which is exactly
    // the behaviour the sync effect relies on to distinguish "loading" from
    // "data ready". One field name change; fixes the cross-conversation
    // contamination on reload + switch.
    currentData: messagesData,
    isLoading,
    error: fetchError,
    refetch,
  } = useGetPersonalMessagesQuery(conversationId, {
    skip: !conversationId,
    refetchOnMountOrArgChange: true,
  });

  const [loadMoreMessages, { isLoading: isLoadingMore }] =
    useLazyLoadMoreMessagesQuery();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [markAsRead] = useMarkAsReadMutation();
  const [clearChat] = useClearChatMutation();
  const [removeUser] = useRemoveUserMutation();

  /**
   * Derive unread count as a primitive to keep handleScroll's useCallback
   * deps stable. Without this, `existingChat` (object reference) changes on
   * every render, recreating the scroll handler on each paint.
   */
  const unreadCount = useMemo(
    () => chats.find((c) => c.otherUserId === conversationId)?.unreadCount ?? 0,
    [chats, conversationId]
  );

  // ── Derive safe display values from atomic msgState ─────────────────────────
  //
  // The render guard (msgState.conversationId === conversationId) is the final
  // line of defence: even if some async path manages to set msgState to the
  // wrong conversation, the render will ALWAYS show [] and hasMore=false until
  // the state is correct. This replaces the previous pattern of separately
  // guarding every setAllMessages / setCursor / setHasMore call.
  const safeMessages = msgState.conversationId === conversationId
    ? msgState.messages
    : [];
  const safeCursor  = msgState.conversationId === conversationId
    ? msgState.cursor
    : null;
  const safeHasMore = msgState.conversationId === conversationId
    ? msgState.hasMore
    : false;

  // ═══════════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════════

  // ── 1. Message-state sync ─────────────────────────────────────────────────
  useEffect(() => {
    if (!conversationId) return;

    const isConversationSwitch = conversationId !== syncedConversationRef.current;

    if (!messagesData) {
      // Fetch in-flight or errored. Write an EMPTY atomic state stamped with
      // the current conversationId. The render guard
      // (msgState.conversationId === conversationId) will immediately show []
      // on the very next paint — no window where stale messages are visible.
      //
      // IMPORTANT: do NOT update syncedConversationRef here (see header comment
      // above for the hard-reload invariant this protects).
      if (isConversationSwitch) {
        setMsgState({ conversationId, messages: [], cursor: null, hasMore: false });
      }
      return;
    }

    if (isConversationSwitch) {
      // ── Conversation switched and data ready → full atomic replace ────────
      syncedConversationRef.current = conversationId;
      setMsgState({
        conversationId,
        messages: messagesData.messages ?? [],
        cursor: messagesData.nextCursor ?? null,
        hasMore: messagesData.hasMore ?? false,
      });
      shouldScrollToBottomRef.current = true;
    } else {
      // ── Same conversation, cache updated → incremental merge ──────────────
      // Covers two cases:
      //   a) SignalR pushed a new message into the RTK cache.
      //   b) An optimistic `temp-xxx` was confirmed with a real server ID.
      //      Stale temp entries are filtered out to prevent double-rendering.
      //
      // SAFETY: snapshot both identifiers before the async functional updater.
      // React 18 can defer functional updaters; by the time the updater runs,
      // syncedConversationRef may have advanced. The guard ensures we never
      // merge one conversation's data into another's.
      const mergeForConversation = conversationId;
      const snapshotMessages = messagesData.messages ?? [];

      setMsgState((prev) => {
  if (syncedConversationRef.current !== mergeForConversation) return prev;
  // Allow null — means the cache was just seeded, this is the first real data
  if (prev.conversationId !== null && prev.conversationId !== mergeForConversation) return prev;

  const cacheIds = new Set(snapshotMessages.map((m) => m.id));

  const map = new Map(
    (prev.messages ?? [])
      .filter((m) => !String(m.id).startsWith('temp-') || cacheIds.has(m.id))
      .map((m) => [m.id, m])
  );

  snapshotMessages.forEach((m) => map.set(m.id, m));

  return {
    conversationId: mergeForConversation, // stamp it now
    messages: Array.from(map.values()).sort(
      (a, b) => new Date(a.sentAt) - new Date(b.sentAt)
    ),
    cursor: prev.cursor,
    hasMore: prev.hasMore,
  };
});
    }
  }, [messagesData, conversationId]);

  // ── 2. UI-only reset on conversation change ────────────────────────────────
  useEffect(() => {
    if (!conversationId) return;
    hasMarkedAsReadRef.current = false;
    setMessage('');
    setMediaFile(null);
    setMediaPreview(null);
    setMediaFileName(null);
    setSendError(null);
    setPaginationError(null);
    setActionError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    inputRef.current?.focus();
  }, [conversationId]);

  // ── 3. Close dropdown on outside click ────────────────────────────────────
  useEffect(() => {
    const onMouseDown = (e) => {
      if (menuWrapperRef.current && !menuWrapperRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // ── 4. Mark as read on mount / conversation change ─────────────────────────
  useEffect(() => {
    if (conversationId && unreadCount > 0 && !hasMarkedAsReadRef.current) {
      markAsRead(conversationId);
      hasMarkedAsReadRef.current = true;
    }
  }, [conversationId, unreadCount, markAsRead]);

  // ── 5. Scroll to bottom ───────────────────────────────────────────────────
  //
  // WHY `allMessages.length` instead of the full `allMessages` array:
  //
  //   Using the whole array as a dep fires on every merge, including when a
  //   message's `isRead` flag updates or a `temp-xxx` ID is swapped for a
  //   real one — both leave the LENGTH unchanged. Those updates must NOT
  //   trigger a scroll (user may be reading older messages).
  //
  //   `.length` changes only when a message is actually added or the list is
  //   replaced (0 → N on conversation switch) — exactly the two cases where
  //   we want to consider scrolling.
  //
  // WHY shouldScrollToBottomRef still works with this dep:
  //   On switch, setAllMessages changes length from 0 → N → effect fires →
  //   shouldScrollToBottomRef is true → instant scroll to bottom ✓
  useEffect(() => {
    if (safeMessages.length === 0) return;

    if (shouldScrollToBottomRef.current) {
      // Conversation switch / initial load — instant, no animation
      shouldScrollToBottomRef.current = false;
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      return;
    }

    // New message arrived while viewing — only scroll if near the bottom
    if (messageAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messageAreaRef.current;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [safeMessages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS  — all wrapped in useCallback for stable references
  // ═══════════════════════════════════════════════════════════════════════════

  /** Mark conversation as read when user scrolls to the very bottom. */
  const handleScroll = useCallback(() => {
    if (!messageAreaRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messageAreaRef.current;
    if (
      scrollHeight - scrollTop - clientHeight < 50 &&
      unreadCount > 0 &&
      !hasMarkedAsReadRef.current
    ) {
      markAsRead(conversationId);
      hasMarkedAsReadRef.current = true;
    }
  }, [conversationId, unreadCount, markAsRead]);

  /** Cursor-based pagination — load and prepend older messages. */
  const handleLoadMore = useCallback(async () => {
    if (!safeHasMore || isLoadingMore || !safeCursor) return;
    setPaginationError(null);

    // Snapshot the conversationId BEFORE the await.
    // If the user switches conversations while the fetch is in-flight,
    // syncedConversationRef will have moved on. We check it after resolution
    // and discard the result to prevent older messages from one conversation
    // being merged into a completely different conversation's message list.
    const loadingForConversation = conversationId;
    prevScrollHeightRef.current = messageAreaRef.current?.scrollHeight ?? 0;

    try {
      const result = await loadMoreMessages(
        { otherUserId: loadingForConversation, cursor: safeCursor }
      ).unwrap();
      if (!result) return;

      // Guard: discard if the user navigated away while this was loading
      if (syncedConversationRef.current !== loadingForConversation) return;

      setMsgState((prev) => {
        // Concurrent-mode safety: validate both guards before merging
        if (
          syncedConversationRef.current !== loadingForConversation ||
          prev.conversationId !== loadingForConversation
        ) return prev;

        const map = new Map();
        // Older messages first so they sort to the top
        [...result.messages, ...prev.messages].forEach((m) => map.set(m.id, m));
        return {
          ...prev,
          messages: Array.from(map.values()).sort(
            (a, b) => new Date(a.sentAt) - new Date(b.sentAt)
          ),
          cursor: result.nextCursor ?? null,
          hasMore: result.hasMore ?? false,
        };
      });

      // Restore scroll position — prepending must not jump the viewport
      requestAnimationFrame(() => {
        if (messageAreaRef.current) {
          messageAreaRef.current.scrollTop =
            messageAreaRef.current.scrollHeight - prevScrollHeightRef.current;
        }
      });
    } catch (err) {
      // Only surface the error if we're still on the same conversation
      if (syncedConversationRef.current === loadingForConversation) {
        console.error('Pagination error:', err);
        setPaginationError('Failed to load older messages. Tap to retry.');
      }
    }
  }, [conversationId, safeCursor, safeHasMore, isLoadingMore, loadMoreMessages]);

  const handleClearChat = useCallback(async () => {
    if (!window.confirm('Delete all messages in this chat?')) return;
    setActionError(null);
    try {
      await clearChat(conversationId).unwrap();
      setMsgState({ conversationId, messages: [], cursor: null, hasMore: false });
      setShowMenu(false);
    } catch (err) {
      console.error('Clear chat failed:', err);
      setActionError('Failed to delete the chat. Please try again.');
    }
  }, [clearChat, conversationId]);

  const handleRemoveUser = useCallback(async () => {
    if (!window.confirm('Remove this user from your chat list?')) return;
    setActionError(null);
    try {
      await removeUser(conversationId).unwrap();
      setShowMenu(false);
      navigate('/chat', { replace: true });
    } catch (err) {
      console.error('Remove user failed:', err);
      setActionError('Failed to remove user. Please try again.');
    }
  }, [removeUser, conversationId, navigate]);

  const handleSend = useCallback(async () => {
    if ((!message.trim() && !mediaFile) || isSending) return;
    setSendError(null);

    // Snapshot state before clearing so we can restore on failure
    const text = message.trim();
    const file = mediaFile;
    const preview = mediaPreview;
    const fileName = mediaFileName;

    // Clear immediately so the UI feels instant
    setMessage('');
    setMediaFile(null);
    setMediaPreview(null);
    setMediaFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      await sendMessage({
        receiverId: conversationId,
        message: text || undefined,
        mediaFile: file || undefined,
      }).unwrap();
      inputRef.current?.focus();

      if (isNewChat && onFirstMessageSent) {
    onFirstMessageSent();
  }

    } catch (err) {
      console.error('Send failed:', err);
      // Restore everything so the user doesn't lose their message
      setMessage(text);
      setMediaFile(file);
      setMediaPreview(preview);
      setMediaFileName(fileName);
      setSendError('Failed to send. Please try again.');
    }
  }, [
    message,
    mediaFile,
    mediaPreview,
    mediaFileName,
    isSending,
    sendMessage,
    conversationId,
  ]);

  /** Enter key handler — stable reference so the input doesn't re-register. */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      setSendError(
        `File too large — maximum is ${MAX_FILE_BYTES / 1024 / 1024} MB.`
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSendError(null);
    setMediaFile(file);
    setMediaFileName(file.name);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setMediaPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      // Non-images show filename in the preview bar — no visual thumbnail
      setMediaPreview(null);
    }
  }, []);

  const clearMedia = useCallback(() => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaFileName(null);
    setSendError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleProfileClick = useCallback(() => {
    if (selectedUser?.id) navigate(`/profile/${selectedUser.id}`);
  }, [navigate, selectedUser?.id]);

  const handleToggleMenu = useCallback(
    () => setShowMenu((prev) => !prev),
    []
  );

  // ── Derived ────────────────────────────────────────────────────────────────
  // Real-time presence from Redux — updated by SignalR UserOnline/UserOffline events
  const isUserOnline = useOnlineStatus(selectedUser?.id);

  const windowClass = [
    styles.chatWindow,
    isMobileVisible ? styles.chatVisible : '',
  ]
    .filter(Boolean)
    .join(' ');

  const canSend = !isSending && (!!message.trim() || !!mediaFile);

  // ═══════════════════════════════════════════════════════════════════════════
  // EARLY RENDER STATES
  // ═══════════════════════════════════════════════════════════════════════════

  if (!conversationId) {
    return (
      <div className={windowClass}>
        <div className={styles.empty}>
          <span aria-hidden="true" style={{ fontSize: 40 }}>💬</span>
          <p>Select a chat to start messaging</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={windowClass}>
        {/* Keep the header fully visible while messages load */}
        <div className={styles.chatHeader}>
          <button
            className={styles.backBtn}
            onClick={onMobileBack}
            aria-label="Back to conversation list"
            type="button"
          >
            ‹
          </button>

          <div className={styles.userInfo}>
            {selectedUser?.profilePhotoUrl ? (
              <img
                src={selectedUser.profilePhotoUrl}
                alt={`${selectedUser.name}'s avatar`}
                className={styles.avatar}
              />
            ) : (
              <div className={styles.avatarPlaceholder} aria-hidden="true">
                {selectedUser?.name?.charAt(0).toUpperCase() ?? '?'}
              </div>
            )}
            <div className={styles.userMeta}>
              <span className={styles.userName}>{selectedUser?.name ?? 'User'}</span>
              <span className={styles.offlineStatus}>Loading…</span>
            </div>
          </div>
        </div>

        {/* Skeleton message bubbles */}
        <div
          className={styles.messageArea}
          role="status"
          aria-label="Loading messages"
          aria-live="polite"
        >
          <div className={styles.messageSpacer} />
          <MessageSkeleton />
        </div>

        {/* Disabled input bar — visible but not interactive during load */}
        <div className={styles.inputContainer}>
          {/* <button className={styles.attachBtn} disabled type="button" aria-hidden="true">
            📎
          </button> */}
          <input
            className={styles.input}
            placeholder="Loading messages…"
            disabled
            aria-label="Message input loading"
          />
          <button className={styles.sendBtn} disabled type="button">
            Send
          </button>
        </div>
      </div>
    );
  }


  if (fetchError) {
    return (
      <div className={windowClass}>
        <div className={styles.empty}>
          <span aria-hidden="true" style={{ fontSize: 36 }}>⚠️</span>
          <p>Failed to load messages.</p>
          <button className={styles.retryBtn} onClick={refetch} type="button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className={windowClass}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={styles.chatHeader}>
        <button
          className={styles.backBtn}
          onClick={onMobileBack}
          aria-label="Back to conversation list"
          type="button"
        >
          ‹
        </button>

        <div className={styles.userInfo}>
          {selectedUser?.profilePhotoUrl ? (
            <img
              src={selectedUser.profilePhotoUrl}
              alt={`${selectedUser.name}'s avatar`}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarPlaceholder} aria-hidden="true">
              {selectedUser?.name?.charAt(0).toUpperCase() ?? '?'}
            </div>
          )}

          <div className={styles.userMeta}>
            <div
              className={styles.userName}
              onClick={handleProfileClick}
              title="View profile"
              aria-label={`View ${selectedUser?.name ?? 'user'}'s profile`}
              type="button"
            >
              {selectedUser?.name ?? 'User'}
            </div>
            <span
              className={isUserOnline ? styles.onlineStatus : styles.offlineStatus}
            >
              {isUserOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Context menu */}
        <div className={styles.menuWrapper} ref={menuWrapperRef}>
          <button
            className={styles.menuBtn}
            onClick={handleToggleMenu}
            aria-label="Chat options"
            aria-haspopup="menu"
            aria-expanded={showMenu}
            type="button"
          >
            ⋮
          </button>

          {showMenu && (
            <div className={styles.dropdown} role="menu">
              <button
                role="menuitem"
                onClick={handleClearChat}
                type="button"
              >
                🗑 Delete Chat
              </button>
              <button
                role="menuitem"
                onClick={handleRemoveUser}
                className={styles.removeOption}
                type="button"
              >
                🚫 Remove User
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Clear / remove action errors */}
      <InlineError
        message={actionError}
        onDismiss={() => setActionError(null)}
      />

      {/* ── Message area ────────────────────────────────────────────────────── */}
      <div
        className={styles.messageArea}
        ref={messageAreaRef}
        onScroll={handleScroll}
        aria-label="Messages"
        aria-live="polite"
      >
        {/*
          Flex spacer — pushes content to the bottom when messages are few.
          Collapses to 0 when messages overflow, letting normal scroll take over.
          Requires: .messageArea { display: flex; flex-direction: column; overflow-y: auto }
        */}
        <div className={styles.messageSpacer} />

        {/* ── Load older messages button ── */}
        {safeHasMore && (
          <div className={styles.loadMoreContainer}>
            {paginationError ? (
              <button
                className={`${styles.loadMoreBtn} ${styles.loadMoreError}`}
                onClick={handleLoadMore}
                type="button"
                aria-label="Retry loading older messages"
              >
                ⚠️ {paginationError}
              </button>
            ) : (
              <button
                className={styles.loadMoreBtn}
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                type="button"
                aria-label="Load older messages"
              >
                {isLoadingMore ? (
                  <><span className={styles.loadMoreSpinner} aria-hidden="true" /> Loading…</>
                ) : (
                  <><span aria-hidden="true">⬆</span> Load older messages</>
                )}
              </button>
            )}
          </div>
        )}

        {/* ── New chat empty state ── */}
        {isTrulyNewChat && safeMessages.length === 0 && (
          <div className={styles.newChatHint}>
            <div className={styles.newChatIcon} aria-hidden="true">👋</div>
            <p>
              Say hello to <strong>{selectedUser?.name}</strong>!
            </p>
          </div>
        )}

        {/* ── Message list ── */}
        {safeMessages.map((msg, index) => {
          const showDateSeparator =
            index === 0 ||
            new Date(safeMessages[index - 1].sentAt).toDateString() !==
              new Date(msg.sentAt).toDateString();

          return (
            <React.Fragment key={msg.id}>
              {showDateSeparator && (
                <div className={styles.dateSeparator}>
                  {formatLocalDate(msg.sentAt)}
                </div>
              )}
              <MessageItem
                msg={msg}
                isMine={msg.senderId === currentUserId}
              />
            </React.Fragment>
          );
        })}

        {/* Scroll anchor — always the last DOM node in the list */}
        <div ref={bottomRef} />
      </div>

      {/* ── Attachment preview bar ──────────────────────────────────────────── */}
      {(mediaPreview || mediaFileName) && (
        <div className={styles.mediaPreview}>
          {mediaPreview ? (
            <img src={mediaPreview} alt="Attachment preview" />
          ) : (
            // Non-image file — show icon + filename
            <div className={styles.filePreview}>
              <span className={styles.fileIcon} aria-hidden="true">📎</span>
              <span className={styles.fileName} title={mediaFileName}>
                {mediaFileName}
              </span>
            </div>
          )}
          <button
            className={styles.clearMediaBtn}
            onClick={clearMedia}
            aria-label="Remove attachment"
            type="button"
          >
            ✕
          </button>
        </div>
      )}

      {/* Send errors — shown directly above the input bar */}
      <InlineError
        message={sendError}
        onDismiss={() => setSendError(null)}
      />

      {/* ── Input bar — hidden for Nessefy Official (one-way channel) ── */}
      {selectedUser?.name === 'Nessefy Official' ? (
        <div className={styles.inputContainer} style={{
          justifyContent: 'center',
          padding: '12px 16px',
          background: '#f8fbfd',
          borderTop: '1px solid #edf2f6',
        }}>
          <p style={{
            margin: 0,
            fontSize: '0.82rem',
            color: '#94a3b8',
            fontFamily: "'DM Sans', sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            🔒 This is an official Nessefy channel. You cannot reply here.
          </p>
        </div>
      ) : (
      <div className={styles.inputContainer}>
        {/* Hidden file input triggered by the attach button */}
        {/* <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          aria-label="Attach a file"
        />

        <button
          className={styles.attachBtn}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach file"
          title="Attach file"
          type="button"
          disabled={!!mediaFile} // prevent selecting a second file while one is pending
        >
          📎
        </button> */}

        <input
          ref={inputRef}
          className={styles.input}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          aria-label="Message input"
        />

        <button
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          type="button"
        >
          {isSending ? (
            <span className={styles.sendingSpinner} aria-hidden="true" />
          ) : (
            'Send'
          )}
        </button>
      </div>
      )} {/* end Nessefy Official check */}
    </div>
  );
}